'use client';

import { useState } from 'react';
import { ChevronRight, Info, ShieldAlert, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { InterferenceAnalysisResponse } from '@/lib/urm-api';
import { workloadKey } from '../logic';

function percent(value: number): string {
  return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function display(value: number | null | undefined, suffix = ''): string {
  return value == null ? 'Insufficient evidence' : `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

export function RateLimitReplayView({
  analysis,
  recalculating,
  onRecalculate,
}: {
  analysis: InterferenceAnalysisResponse;
  recalculating: boolean;
  onRecalculate: (weights: Record<string, number>) => Promise<void>;
}) {
  const [weights, setWeights] = useState<Record<string, string>>(() => Object.fromEntries(
    analysis.workload_costs.map((cost) => [workloadKey(cost.workload), String(cost.weight)]),
  ));
  const [weightError, setWeightError] = useState<string | null>(null);
  const replay = analysis.rate_limit_replay;
  const validation = analysis.validation;
  const highNormalImpact = replay.available && replay.normal_traffic_affected >= 0.25;

  const submit = async () => {
    const parsed: Record<string, number> = {};
    for (const [key, raw] of Object.entries(weights)) {
      const value = Number(raw);
      if (!Number.isFinite(value) || value <= 0) {
        setWeightError(`Weight for ${key} must be a positive number.`);
        return;
      }
      parsed[key] = value;
    }
    setWeightError(null);
    try {
      await onRecalculate(parsed);
    } catch {
      // The page-level API error contains the backend or CSRF detail.
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Replay shows that the weighted limiter would have reacted before an event. It does not prove that throttling would have prevented the event.</p>
      </div>

      {replay.available && (
        <div className={`flex gap-2 rounded-lg border px-4 py-3 text-sm ${highNormalImpact ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100' : 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100'}`}>
          {highNormalImpact ? <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /> : <Info className="mt-0.5 h-4 w-4 shrink-0" />}
          <div><p className="font-semibold">{highNormalImpact ? 'High impact on normal traffic' : 'Low simulated impact on normal traffic'}</p><p className="mt-1">This simulation catches {percent(replay.event_coverage)} of incident periods, while affecting {percent(replay.normal_traffic_affected)} of traffic outside those incidents.{!validation.validated ? ' The result has not been validated on a later time period.' : ''}</p></div>
        </div>
      )}

      {!replay.available ? (
        <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />Replay is unavailable for this range. Rate-limit samples or degradation incidents may be missing.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Incident coverage" value={percent(replay.event_coverage)} detail={`${replay.events_with_pre_event_throttling} caught · ${replay.events_missed} missed`} />
          <Metric label="Normal traffic affected" value={percent(replay.normal_traffic_affected)} detail="Traffic throttled outside incident periods" />
          <Metric label="Healthy periods throttled" value={percent(replay.healthy_periods_throttled)} detail="Healthy 30-second periods with throttling" />
          <Metric label="Median warning time" value={display(replay.median_lead_time_seconds, 's')} detail="How early throttling began before an incident" />
        </div>
      )}

      <details className="group rounded-lg border bg-card shadow-sm">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />Advanced: test custom workload weights</summary>
        <div className="border-t px-4 py-4">
          <p className="mb-4 text-sm text-muted-foreground">Change relative costs and rerun the simulation. The backend reuses cached metrics for the selected time range. This does not modify the live limiter.</p>
          {analysis.workload_costs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No candidate weights are available to edit.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {analysis.workload_costs.map((cost) => {
                const key = workloadKey(cost.workload);
                return (
                  <label key={key} className="rounded-lg border p-3">
                    <span className="block break-all font-mono text-xs">{key}</span>
                    <span className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      Weight
                      <input
                        aria-label={`Weight for ${key}`}
                        type="number" min="0.01" step="0.1"
                        value={weights[key] ?? ''}
                        onChange={(event) => setWeights((current) => ({ ...current, [key]: event.target.value }))}
                        className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm text-foreground"
                      />
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {weightError && <p role="alert" className="mt-3 text-sm text-destructive">{weightError}</p>}
          <Button className="mt-4" onClick={() => void submit()} disabled={recalculating || analysis.workload_costs.length === 0}>
            {recalculating ? 'Recalculating…' : 'Recalculate replay'}
          </Button>
        </div>
      </details>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Chronological validation</CardTitle>
          <CardDescription>Earlier observations train the model; later observations validate it. Past and future intervals are never randomly mixed.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label="Status" value={validation.validated ? 'Validated' : 'Not validated'} />
            <Metric label="Validation AUC" value={validation.auc == null ? 'Insufficient evidence' : validation.auc.toFixed(3)} />
            <Metric label="Training / validation incidents" value={`${validation.training_incidents} / ${validation.validation_incidents}`} />
          </div>
          {!validation.validated && (
            <div className="mt-3 flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />{validation.reason || 'There are too few incidents for a separate chronological validation period.'}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return <div className="rounded-lg border bg-card p-4 shadow-sm"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 text-xl font-semibold">{value}</p>{detail && <p className="mt-1 text-xs leading-5 text-muted-foreground">{detail}</p>}</div>;
}
